import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPenNib,
  faRightToBracket,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";

import { useAuth } from "../../auth/AuthContext";
import { useT } from "../../i18n/I18nContext";

const STORAGE_KEY = "writeon:dismissed_signup_banner";

// Slim, themed strip shown below the navbar for anonymous visitors —
// pitches sign-up + offers a log-in shortcut without taking over the
// page. Dismissal is persisted in localStorage so closing it once
// keeps it gone across page navigations and reloads. The strip
// short-circuits whenever:
//
//   * AuthContext is still booting (status === "loading") — avoids a
//     flicker on every page load before /api/auth/me/ resolves.
//   * The visitor is signed in.
//   * They've already dismissed it once.
export default function GuestSignupBanner() {
  const t = useT();
  const { user, status } = useAuth();
  const [dismissed, setDismissed] = useState(() => {
    if (typeof window === "undefined") return false;
    try {
      return window.localStorage.getItem(STORAGE_KEY) === "1";
    } catch {
      return false;
    }
  });

  // Re-check on auth changes — if a writer logs in and back out we
  // honour the previous dismissal rather than re-popping the strip.
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      setDismissed(window.localStorage.getItem(STORAGE_KEY) === "1");
    } catch {
      /* ignore */
    }
  }, [user]);

  const dismiss = () => {
    setDismissed(true);
    try {
      window.localStorage.setItem(STORAGE_KEY, "1");
    } catch {
      /* ignore */
    }
  };

  if (status === "loading" || user || dismissed) return null;

  return (
    <div className="relative bg-gradient-to-r from-primary/10 via-primaryMuted to-primary/10 text-text border-b border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-2.5">
        {/* Two-row mobile layout / single-row desktop. The
            mobile shape is:
              [icon] [headline + subhead]                [X]
              [        Sign up        ] [    Log in    ]
            Desktop collapses back to the original single horizontal
            strip via sm: utilities. The X button sits absolutely on
            mobile so the headline can use the full width without
            having to wrap around it. */}
        <div className="flex items-start gap-3 sm:items-center sm:flex-nowrap">
          <span className="inline-flex items-center justify-center h-10 w-10 sm:h-8 sm:w-8 rounded-full bg-primary text-white shadow-sm shrink-0">
            <FontAwesomeIcon icon={faPenNib} className="text-base sm:text-sm" />
          </span>
          <div className="min-w-0 flex-1 pr-8 sm:pr-0">
            <p className="text-sm sm:text-sm leading-snug">
              <span className="font-semibold text-text block sm:inline">
                {t("guest_banner.headline")}
              </span>{" "}
              <span className="text-secondText block sm:inline">
                {t("guest_banner.subhead")}
              </span>
            </p>
          </div>
          <div className="hidden sm:flex items-center gap-2 shrink-0">
            <Link
              to="/register"
              className="inline-flex items-center gap-1.5 rounded-full bg-primary text-white px-4 py-1.5 text-sm font-semibold hover:brightness-110 transition focus:outline-none focus:ring-2 focus:ring-primary/40"
            >
              {t("guest_banner.signup")}
            </Link>
            <Link
              to="/login"
              className="inline-flex items-center gap-1.5 rounded-full border border-border bg-surface px-4 py-1.5 text-sm font-semibold text-text hover:border-primary hover:text-primary transition"
            >
              <FontAwesomeIcon icon={faRightToBracket} className="text-xs" aria-hidden="true" />
              {t("guest_banner.login")}
            </Link>
          </div>
          <button
            type="button"
            onClick={dismiss}
            aria-label={t("guest_banner.dismiss")}
            title={t("guest_banner.dismiss")}
            className="absolute top-2 right-2 sm:static sm:ml-1 inline-flex items-center justify-center h-8 w-8 rounded-full text-secondText hover:bg-surface hover:text-text transition focus:outline-none focus:ring-2 focus:ring-primary/30 shrink-0"
          >
            <FontAwesomeIcon icon={faXmark} aria-hidden="true" />
          </button>
        </div>

        {/* Mobile-only CTA row — full-width split gives both buttons
            equal real estate. Padding matches the rest of the app's
            mobile CTAs (sidebar Login / Register pair, etc.) so the
            strip doesn't read as oversized. ``px-3 py-2 text-sm`` is
            the canonical mobile button size in this codebase. */}
        <div className="flex sm:hidden gap-2 mt-3">
          <Link
            to="/register"
            className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full bg-primary text-white px-3 py-2 text-sm font-semibold hover:brightness-110 transition focus:outline-none focus:ring-2 focus:ring-primary/40"
          >
            {t("guest_banner.signup")}
          </Link>
          <Link
            to="/login"
            className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full border border-border bg-surface px-3 py-2 text-sm font-semibold text-text hover:border-primary hover:text-primary transition"
          >
            <FontAwesomeIcon icon={faRightToBracket} className="text-xs" aria-hidden="true" />
            {t("guest_banner.login")}
          </Link>
        </div>
      </div>
    </div>
  );
}
